import { useSyncExternalStore } from 'react'

type ChatRequestListener = (message: string) => void
type BusyListener = () => void

let listener: ChatRequestListener | null = null
let isBusy = false
const busyListeners = new Set<BusyListener>()

/**
 * AL renders as a sibling of the comparison table and keeps `isOpen` in its own state, so
 * nothing in the table can reach it through props or context without threading a provider
 * through the whole page. This is the seam instead: the table asks for a question to be
 * put to AL, and AL — wherever it is mounted — opens itself and sends it.
 *
 * A single listener, not a set: exactly one AiAgent is mounted at a time, and a second
 * subscriber would mean two chats answering the same request.
 */
export function subscribeToChatRequests(fn: ChatRequestListener): () => void {
  listener = fn
  return () => {
    if (listener === fn) listener = null
  }
}

/** Opens AL, if it is mounted, and sends `message` once it is ready to take one. */
export function requestChatWithMessage(message: string): void {
  listener?.(message)
}

/**
 * AL publishes whether he is mid-answer so the buttons that ask him things can go inactive
 * while he is. Without it every extra click was another identical question, and AL
 * generated the same report over and over.
 */
export function setChatBusy(next: boolean): void {
  if (isBusy === next) return
  isBusy = next
  busyListeners.forEach((notify) => notify())
}

function subscribeToChatBusy(fn: BusyListener): () => void {
  busyListeners.add(fn)
  return () => {
    busyListeners.delete(fn)
  }
}

function getChatBusy(): boolean {
  return isBusy
}

/** True while AL is generating an answer. */
export function useChatBusy(): boolean {
  return useSyncExternalStore(subscribeToChatBusy, getChatBusy, getChatBusy)
}
